import { formatISO } from 'date-fns';
import { getLocaleNumberSeparator } from '@translations/functions';



export const capitalizeFirstChar = (str: string) => {
	if (!str || typeof str !== 'string' || str.length === 0) {
		return str;
	}
	let firstChar = str.substring(0, 1);
	let remainingChars = str.slice(1);
	return `${firstChar.toUpperCase()}${remainingChars}`;
}

export const isValidEmail = (email: string | null | undefined) =>
	email ? /^\S+@\S+\.\w+$/.test(email) : false;

export const isMaxDate = (date: string | null | undefined) =>
	date ? /^9999-12-31*/.test(date) : false;

export const isMinDate = (date: string | null | undefined) =>
	date ? /^0001-01-01*/.test(date) : false;

export const checkDecimalInput = (numberString: string | null | undefined) =>
	numberString ? /^[1-9]+\d*[,.]?\d{0,2}$/.test(numberString) : false;

export const checkIntegerInput = (numberString: string | null | undefined) =>
	numberString ? /^[1-9]\d*$/.test(numberString) : false;

export const isValidPhoneNumber = (numberString: string | null | undefined) =>
	numberString ? /\+?[\d\s]+\d$/.test(numberString) : false;

export const exist = (value: unknown | null | undefined) =>
	(value !== undefined && value !== null) ? true : false;

export const isEmpty = (value: unknown | null | undefined) => {
	if (value === null || value === undefined) return true;
	if (typeof value === 'string' && value === '') return true;
	if (typeof value === 'number' && value === 0) return true;
	if (Number.isNaN(value)) return true;
	return false;
}

export const getDefaultNumber = (value: number | null | undefined): number => {
	if (value === null || value === undefined) return 0;
	if (Number.isNaN(value)) return 0;
	if (typeof value === 'number') return value;
	return 0;
}

export const isInteger = (value: number) => {
	if (!Number.isNaN(value) && isFinite(value) && value > -2147483648 && value < 2147483647)
		return true;
	return false;
}

export const isDouble = (value: number) => {
	if (!Number.isNaN(value) && value > 4.94065645841247E-324 && value < 1.7976931348623157e+308)
		return true;
	return false;
}

export const checkIntegerInputAndMaxLength = (numberString: string | null | undefined, maxLength: number) => {
	if (numberString === null || numberString === undefined || numberString === '')
		return false;
	
	let isValid = checkIntegerInput(numberString);
	if (isValid && numberString.length <= maxLength)
		return true;
	
	return false;
}

export const roundNumberOneDecimal = (value: number): string => {
	let roundedNumber = Math.round((value + Number.EPSILON) * 100) / 100;
	return getLocaleNumberSeparator(String(roundedNumber));
}

type IntegerInputAndMaxValueType = { success: true, parsedValue: number } | { success: false, parsedValue: null }

export const checkIntegerInputAndMaxValue = (numberString: string | null | undefined, maxLength?: number): IntegerInputAndMaxValueType => {
	if (numberString === null || numberString === undefined || numberString === '')
		return { success: false, parsedValue: null };
	
	let isValid = checkIntegerInput(numberString);
	if (!isValid)
		return { success: false, parsedValue: null };

	let parsedValue = parseInt(numberString);
	if (maxLength === undefined && isInteger(parsedValue))
		return { success: true, parsedValue: parsedValue };

	if (maxLength !== undefined && isInteger(parsedValue) && parsedValue <= maxLength)
		return { success: true, parsedValue: parsedValue };

	return { success: false, parsedValue: null };
}


type DecimalInputAndMaxValueType = { success: true, parsedValue: string } | { success: false, parsedValue: null }

export const checkDecimalInputAndMaxValue = (numberString: string | null | undefined, maxStringLength?: number, maxNumberLength?: number): DecimalInputAndMaxValueType => {
	
	if (numberString === null || numberString === undefined || numberString === '')
		return { success: false, parsedValue: null };
	
	let isValid = checkDecimalInput(numberString);
	if (!isValid)
		return { success: false, parsedValue: null };

	let replacedString = numberString.replace(',', '.');
	let parsedValue = parseFloat(replacedString);
	switch (true) {
		case (!isDouble(parsedValue)):
			return { success: false, parsedValue: null };
		
		case (maxStringLength === undefined && maxNumberLength === undefined):
			return { success: true, parsedValue: replacedString };
		
		case (maxStringLength === undefined && maxNumberLength !== undefined && parsedValue <= maxNumberLength):
			return { success: true, parsedValue: replacedString };

		case (maxStringLength !== undefined && maxNumberLength === undefined && replacedString.length <= maxStringLength):
			return { success: true, parsedValue: replacedString };
		
		case (maxStringLength !== undefined && maxNumberLength !== undefined && replacedString.length <= maxStringLength && parsedValue <= maxNumberLength):
			return { success: true, parsedValue: replacedString };
		
		default:
			return { success: false, parsedValue: null };
	}
}


export function create_UUID(){
    let timeValue = new Date().getTime();
    let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        let r = (timeValue + Math.random() * 16) %16 | 0;
        timeValue = Math.floor(timeValue/16);
        return (c == 'x' ? r :(r&0x3|0x8)).toString(16);
    });
    return uuid;
}

export function numberWithSpaces(value: string | number) {
	let stringValue = String(value)
	let [ integerPart, ...rest ] = stringValue.split(".");
	if (!integerPart) return stringValue;
	
	let integerWithSpaces = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return integerWithSpaces + rest.join(".");
}



type ConvertUnitOptions = ConvertUnitOptionsNumber | ConvertUnitOptionsString;

type ConvertUnitOptionsNumber = {
	decimalPlaces?: number;
	output?: 'number';
}

type ConvertUnitOptionsString = {
	decimalPlaces?: number;
	output: 'string';
	withSpaces?: boolean;
}
  


export function kilogramsToTonnes(value: number, options?: ConvertUnitOptionsNumber): number;
export function kilogramsToTonnes(value: number, options: ConvertUnitOptionsString): string;
export function kilogramsToTonnes(value: number, options: ConvertUnitOptions = {}): number | string {
	const decimalPlaces = options.decimalPlaces ?? 0;
	const output = options.output ?? 'number';
	const withSpaces = 'withSpaces' in options ? options.withSpaces : false;

	if (!value) return output === 'string' ? '0' : 0;

	const tonnes = value / 1000;
	const rounded = decimalPlaces === 0
		? Math.round(tonnes)
		: Number(tonnes.toFixed(decimalPlaces));

	if (output === 'number') return rounded;

	const formattedString = getLocaleNumberSeparator(rounded.toString());

	return withSpaces
		? numberWithSpaces(formattedString)
		: formattedString;
}

export const renderKilogramsToTonnes = (value: number, decimalPlaces?: number) =>
	kilogramsToTonnes(value, { output: 'string', withSpaces: true, decimalPlaces: decimalPlaces ?? 0 });



export function litresToHL(value: number, options?: ConvertUnitOptionsNumber): number;
export function litresToHL(value: number, options: ConvertUnitOptionsString): string;
export function litresToHL(value: number, options: ConvertUnitOptions = {}): number | string {
	const decimalPlaces = options.decimalPlaces ?? 0;
	const output = options.output ?? 'number';
	const withSpaces = 'withSpaces' in options ? options.withSpaces : false;

	if (!value) return output === 'string' ? '0' : 0;

	const hlValue = value / 100;
	const rounded = decimalPlaces === 0
		? Math.round(hlValue)
		: Number(hlValue.toFixed(decimalPlaces));

	if (output === 'number') return rounded;

	const formattedString = getLocaleNumberSeparator(rounded.toString());

	return withSpaces
		? numberWithSpaces(formattedString)
		: formattedString;
}

export const renderLitresToHL = (value: number, decimalPlaces?: number) =>
	litresToHL(value, { output: 'string', withSpaces: true, decimalPlaces: decimalPlaces ?? 0 });



export const getDateWithoutTime = (date: string | null | undefined) => {
	if (date && date.length > 9) {
		const result = date.match(/\d{4}-\d{2}-\d{2}/);
		if (result) return result[0];
	}
	return '';
}

export const getDateWithoutTimeFromDate = (date: Date) => {
	if (date) {
		let isoDate = formatISO(date);
		const result = isoDate.match(/\d{4}-\d{2}-\d{2}/);
		if (result) return result[0];
	}
	return '';
}


export function replaceAt(baseString: string, index: number, replacement: string) {
	if(index > baseString.length - 1) {
		return baseString;
	}
	return baseString.substring(0, index) + replacement + baseString.substring(index + 1);
}



export function getGridSummaryCount<T>(data: T[] | null | undefined): string {
	if (!data) return '';
	return `Počet záznamů: ${numberWithSpaces(data.length)}`;
}


type GridSummaryOptions = {
	decimalPlaces?: number,
	unit?: string,
	unitConversion?: (value: number) => number,
	withSpaces?: boolean;
}

export function getGridSummarySum<T>(data: T[] | null | undefined, property: keyof T, options: GridSummaryOptions = {}): string {
	if (!data || !property) return '';

	const decimalPlaces = options.decimalPlaces ?? 0;
	const withSpaces = 'withSpaces' in options ? options.withSpaces : false;

	const sum = data.reduce((total, row) => {
		const value = Number(String(row[property]).replace(',', '.')) || 0;
		return total + value;
	}, 0);

	const convertedSum = options.unitConversion ? options.unitConversion(sum) : sum;
	const roundedSum = Number(convertedSum.toFixed(decimalPlaces));

	const formattedString = getLocaleNumberSeparator(roundedSum.toString());
	const formattedSum = withSpaces ? numberWithSpaces(formattedString) : formattedString;
	
	return options.unit ? `${formattedSum} ${options.unit}` : formattedSum;
}

export const renderGridSummarySum = <T>(data: T[] | null | undefined, property: keyof T, decimalPlaces?: number ) =>
	getGridSummarySum(data, property, { withSpaces: true, decimalPlaces: decimalPlaces ?? 0 });

export const renderGridSummarySumTonnes = <T>(data: T[] | null | undefined, property: keyof T, decimalPlaces?: number ) =>
	getGridSummarySum(data, property, { unit: 't', unitConversion: (value) => value / 1000, withSpaces: true, decimalPlaces: decimalPlaces ?? 0 });

export const renderGridSummarySumHL = <T>(data: T[] | null | undefined, property: keyof T, decimalPlaces?: number ) =>
	getGridSummarySum(data, property, { unit: 'hl', unitConversion: (value) => value / 100, withSpaces: true, decimalPlaces: decimalPlaces ?? 0 });

export const renderGridSummarySumVolumeAndCount = <T>(data: T[] | null | undefined, volumeProperty: keyof T, countProperty: keyof T, decimalPlaces?: number ) => {
	if (!data) return '';
	return `${
		getGridSummarySum(data, volumeProperty, { unitConversion: (value) => value / 100, withSpaces: true, decimalPlaces: decimalPlaces ?? 0 })
	} / ${
		getGridSummarySum(data, countProperty, { withSpaces: true, decimalPlaces: decimalPlaces ?? 0 })
	}`;
}


export const formatPhoneNumber = (number: string) => {
	
	if (number.length > 9) {
		const prefix = number.slice(0, -9);
		const lastNine = number.slice(-9);
		const formattedLastNine = lastNine.match(/.{3}/g)?.join(' ') || lastNine;
	
		return `${prefix} ${formattedLastNine}`;
	}
	
	const formattedNumber = number.match(/.{3}/g)?.join(' ') || number;
	return formattedNumber;
};